import { setFunnelData } from 'actions/proposal';
import { trackFunnelData } from 'actions/tracking';
import { ActionButton, Col, Icon, Markdown, Message, Row, Text } from 'components';
import PrefetchImage from 'components/PrefetchImage';
import * as t from 'data';
import { Form, Formik, FormikErrors } from 'formik';
import { InputField, EmailField, SelectField, PhoneField, RadioGroupField } from 'forms/fields';
import { salutationOptions, callbackTimeOptions } from 'forms/options';
import { contactDetailsSchema } from 'helpers';
import { useDispatch, useHistory, useSelector } from 'hooks';
import { applyCyAttribute, css, styled } from 'mymoria-ui';
import theme from 'mymoria-ui/styles/theme';
import { FunctionComponent, KeyboardEvent, useRef } from 'react';
import { ContactDetails } from 'types';
import { cyNameDataStep, sleep } from 'utils';
import { ANIMATION_DELAY, FunnelStepProps } from '../Funnel';

const StyledActionButton = styled(ActionButton)`
  width: 100%;
`;

const Terms = styled(Markdown)(
  ({ theme: { fontSizes } }) => css`
    a {
      text-decoration: underline;
    }

    text-align: center;
    p {
      font-size: ${fontSizes.tiny};
    }
  `,
);

const ContactDetailsStep: FunctionComponent<FunnelStepProps> = ({ onStepLoad }) => {
  const { push: historyPush } = useHistory();
  const dispatch = useDispatch();
  const {
    salutation,
    firstname = '',
    lastname = '',
    email = '',
    phone = '',
    concernType,
  } = useSelector(({ proposal }) => proposal);
  const oppOwner = useSelector(({ tracking }) => tracking.oppOwner);
  onStepLoad();

  const phoneFieldColRef = useRef<HTMLSelectElement>(null);

  const scrollToError = (errors: FormikErrors<ContactDetails>) => {
    const fieldOrder = ['salutation', 'firstname', 'lastname', 'email', 'phone'] as const;
    const key = fieldOrder.find(fieldName => errors[fieldName]);

    if (!key) return;

    const element = document.getElementsByName(key)[0];
    if (element) {
      if (key === 'salutation') {
        element.parentElement?.parentElement?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      } else {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        element.focus({ preventScroll: true });
      }
    }
  };

  const handleSubmit = async (contactDetails: ContactDetails) => {
    dispatch(setFunnelData(contactDetails));
    dispatch(trackFunnelData(contactDetails));

    await sleep(ANIMATION_DELAY);
    // clean landing page url cookie
    historyPush('/offer/prepare');
  };

  const handleTabKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Tab' && phoneFieldColRef.current) {
      const countrySelect = phoneFieldColRef.current.querySelector('select');
      countrySelect && countrySelect.focus();
    }
  };

  return (
    <div>
      <Formik
        onSubmit={handleSubmit}
        initialValues={{
          email,
          firstname,
          lastname,
          phone,
          salutation,
        }}
        validationSchema={contactDetailsSchema}
      >
        {({ submitForm, isSubmitting, errors }) => (
          <Form noValidate>
            <Col px={0} mx={['0', null, '130px', '150px']} alignItems="center">
              <Col flexWrap="wrap" justifyContent="center" width={1} px={0} order={[2, null, 1]}>
                <Col width={1} px={0}>
                  <Row py={3}>
                    <RadioGroupField
                      name="salutation"
                      values={salutationOptions}
                      label={t.form.salutationLabel}
                      cyName={`${cyNameDataStep}-salutation`}
                    />
                  </Row>

                  <Row flexWrap="wrap" mx={-2}>
                    <Col width={1} py={1} px={2}>
                      <InputField
                        name="firstname"
                        label={t.form.firstname}
                        cyName={`${cyNameDataStep}-firstname`}
                      />
                    </Col>
                    <Col width={1} py={1} px={2}>
                      <InputField
                        name="lastname"
                        label={t.form.lastname}
                        cyName={`${cyNameDataStep}-lastname`}
                      />
                    </Col>
                  </Row>

                  <Row flexWrap="wrap" mx={-2}>
                    <Col width={1} py={1} px={2}>
                      <EmailField
                        name="email"
                        label={t.form.email}
                        type="email"
                        inputMode="email"
                        placeholder={t.form.emailPlaceholder}
                        onKeyDownCapture={handleTabKey}
                        cyName={cyNameDataStep}
                      />
                    </Col>
                    <Col ref={phoneFieldColRef} width={1} py={1} px={2}>
                      <PhoneField
                        name="phone"
                        label={t.form.phone}
                        placeholder={t.form.phonePlaceholder}
                        cyName={cyNameDataStep}
                      />
                    </Col>
                  </Row>
                  {concernType === 'coming' && (
                    <Row flexWrap="wrap" justifyContent="center" mx={-2}>
                      <Col width={[1, 1, 1]} py={1} px={2}>
                        <SelectField
                          name="callbackTime"
                          label={t.form.callbackTime.label}
                          placeholder={t.form.callbackTime.placeholder}
                          options={callbackTimeOptions}
                          cyName={cyNameDataStep}
                        />
                      </Col>
                    </Row>
                  )}

                  <Col flexWrap="wrap">
                    {oppOwner && <Message>{t.funnel.contactDetails.noAutomaticEmailSent}</Message>}
                    <Row width={1} justifyContent="center" alignItems="center" px={2} my={0}>
                      <Icon.Lock color={theme.colors.primary} width={12} height={12} />
                      <Text
                        textAlign="center"
                        fontSize="tiny"
                        ml={1}
                        {...applyCyAttribute('secureData')}
                      >
                        {t.funnel.contactDetails.secureData}
                      </Text>
                    </Row>
                    <Col width={1} py={1} px={2}>
                      <StyledActionButton onClick={submitForm} disabled={isSubmitting} shadow>
                        <Text fontWeight="bold" color="secondary">
                          {t.funnel.contactDetails.submitTitle}
                        </Text>
                        <Text fontWeight="normal" color="secondary">
                          {t.funnel.contactDetails.submitSubtitle}
                        </Text>
                      </StyledActionButton>
                    </Col>
                    <Col width={1} py={1} px={2}>
                      <Terms m={0} linkTarget="_blank" {...applyCyAttribute('Terms')}>
                        {t.funnel.contactDetails.terms}
                      </Terms>
                    </Col>
                  </Col>
                </Col>
              </Col>
            </Col>
            {isSubmitting && Object.keys(errors).length > 0 && scrollToError(errors)}
          </Form>
        )}
      </Formik>
      <PrefetchImage alt="AnimationScreenImage-1" src={t.funnelWaitingScreens.steps[0].asset} />
    </div>
  );
};

export default ContactDetailsStep;
